import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, Tag, Store, Phone, Clock, AlertTriangle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Discounts({ user }) {
  const [discounts, setDiscounts] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [showQRDialog, setShowQRDialog] = useState(false);
  
  // Disclaimer state
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [disclaimerText, setDisclaimerText] = useState("");
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [disclaimerChecking, setDisclaimerChecking] = useState(true);

  useEffect(() => {
    checkDisclaimer();
  }, []);

  const checkDisclaimer = async () => {
    try {
      setDisclaimerChecking(true);
      const response = await axios.get(`${API}/discounts/disclaimer/status`, { withCredentials: true });
      
      if (response.data.needs_disclaimer) {
        setDisclaimerText(response.data.disclaimer_text);
        setShowDisclaimer(true);
      } else {
        // Disclaimer not needed, load content
        fetchDiscounts();
        fetchMerchants();
      }
    } catch (error) {
      console.error("Error checking disclaimer:", error);
      // On error, just load the content
      fetchDiscounts();
      fetchMerchants();
    } finally {
      setDisclaimerChecking(false);
    }
  };

  const handleAcceptDisclaimer = async () => {
    try {
      await axios.post(`${API}/discounts/disclaimer/accept`, {
        dont_show_again: dontShowAgain
      }, { withCredentials: true });
      
      setShowDisclaimer(false);
      toast.success("Thank you for acknowledging the disclaimer");
      
      // Now load the content
      fetchDiscounts();
      fetchMerchants();
    } catch (error) {
      console.error("Error accepting disclaimer:", error);
      toast.error("Failed to accept disclaimer. Please try again.");
    }
  };

  const fetchDiscounts = async () => {
    try {
      const response = await axios.get(`${API}/discounts`);
      setDiscounts(response.data);
    } catch (error) {
      console.error("Error fetching discounts:", error);
      toast.error("Failed to load discounts");
    } finally {
      setLoading(false);
    }
  };

  const fetchMerchants = async () => {
    try {
      const response = await axios.get(`${API}/merchants`);
      setMerchants(response.data);
    } catch (error) {
      console.error("Error fetching merchants:", error);
    }
  };

  const generateQRCode = async (discount) => {
    try {
      const response = await axios.post(
        `${API}/discounts/${discount.id}/generate-qr`,
        {},
        { withCredentials: true }
      );
      setQrCode(response.data.qr_code);
      setSelectedDiscount(discount);
      setShowQRDialog(true);
      toast.success("QR code generated! Show this to the merchant.");
    } catch (error) {
      console.error("Error generating QR:", error);
      toast.error(error.response?.data?.detail || "Failed to generate QR code");
    }
  };

  const getMerchantName = (merchantId) => {
    const merchant = merchants.find(m => m.id === merchantId);
    return merchant?.business_name || "Merchant";
  };

  // Show loading while checking disclaimer
  if (disclaimerChecking) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Show disclaimer modal
  if (showDisclaimer) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center p-4">
        <Card className="max-w-lg w-full bg-slate-900/90 border-2 border-yellow-500/30 shadow-2xl">
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-12 h-12 bg-yellow-500/20 rounded-full">
                <AlertTriangle className="w-6 h-6 text-yellow-500" />
              </div>
              <h2 className="text-xl font-bold text-white">Important Disclaimer</h2>
            </div>
            
            {/* Disclaimer Text */}
            <div className="bg-slate-800/50 rounded-lg p-4 mb-6 max-h-64 overflow-y-auto">
              <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">
                {disclaimerText}
              </p>
            </div>
            
            {/* Don't show again checkbox */}
            <div className="flex items-start gap-3 mb-6 p-3 bg-slate-800/30 rounded-lg">
              <Checkbox
                id="dont-show-again"
                checked={dontShowAgain}
                onCheckedChange={setDontShowAgain}
                className="mt-0.5"
              />
              <label 
                htmlFor="dont-show-again" 
                className="text-sm text-slate-300 cursor-pointer leading-relaxed"
              >
                Don&apos;t show this disclaimer again for the next 6 months
              </label>
            </div>
            
            {/* Accept Button */}
            <Button
              onClick={handleAcceptDisclaimer}
              className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-semibold py-3"
              data-testid="accept-disclaimer-btn"
            >
              I Understand & Agree
            </Button>
            
            {/* Back link */}
            <div className="mt-4 text-center">
              <Link to="/dashboard" className="text-sm text-slate-400 hover:text-slate-300">
                ← Return to Dashboard
              </Link>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <header className="bg-slate-950 border-b border-white/5">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/dashboard">
            <Button variant="ghost" className="text-yellow-400 hover:text-yellow-300" data-testid="back-button">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
          </Link>
          <h1 className="text-2xl font-bold text-yellow-400 uppercase" style={{ fontFamily: "'Times New Roman', Times, Georgia, serif", letterSpacing: "0.05em" }}>SCC Football</h1>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <h2 className="text-4xl font-bold text-white mb-8" data-testid="page-title">Member Discounts</h2>

        {discounts.length === 0 ? (
          <Card className="p-8 text-center" data-testid="no-discounts-message">
            <p className="text-slate-400">No discounts available yet. Check back soon!</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {discounts.map((discount) => (
              <Card
                key={discount.id}
                className="bg-slate-900/50 border-2 border-white/5 shadow-lg card-hover overflow-hidden"
                data-testid={`discount-card-${discount.id}`}
              >
                {/* Product Image */}
                {discount.image_url && (
                  <div className="w-full h-40 bg-slate-800">
                    <img 
                      src={discount.image_url} 
                      alt={discount.title}
                      className="w-full h-full object-cover"
                      onError={(e) => e.target.parentElement.style.display = 'none'}
                    />
                  </div>
                )}
                
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex items-center justify-center w-12 h-12 bg-yellow-500/20 rounded-full">
                      <Store className="w-6 h-6 text-yellow-500" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-400" data-testid={`merchant-name-${discount.id}`}>{getMerchantName(discount.merchant_id)}</p>
                      <h3 className="text-lg font-bold text-white" data-testid={`discount-title-${discount.id}`}>{discount.title}</h3>
                    </div>
                  </div>

                  <p className="text-slate-300 mb-4" data-testid={`discount-description-${discount.id}`}>{discount.description}</p>

                  <div className="space-y-2 mb-4">
                    {discount.discount_percentage && (
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4 text-yellow-500" />
                        <span className="font-bold text-yellow-500" data-testid={`discount-percentage-${discount.id}`}>
                          {discount.discount_percentage}% OFF
                        </span>
                      </div>
                    )}
                    
                    {/* Price Display */}
                    {(discount.original_price || discount.discounted_price) && (
                      <div className="flex items-center gap-3 my-2">
                        {discount.original_price && (
                          <span className="text-slate-400 line-through text-sm">
                            ${discount.original_price.toFixed(2)}
                          </span>
                        )}
                        {discount.discounted_price && (
                          <span className="text-emerald-400 font-bold text-lg">
                            ${discount.discounted_price.toFixed(2)}
                          </span>
                        )}
                      </div>
                    )}
                    
                    <p className="text-sm text-slate-400">
                      Code: <span className="font-mono font-semibold" data-testid={`discount-code-${discount.id}`}>{discount.discount_code}</span>
                    </p>
                    {discount.valid_until && (
                      <p className="text-sm text-slate-400" data-testid={`discount-valid-until-${discount.id}`}>
                        Valid until: {new Date(discount.valid_until).toLocaleDateString()}
                      </p>
                    )}
                    <p className="text-sm text-slate-400" data-testid={`discount-max-uses-${discount.id}`}>
                      Max uses: {discount.max_uses_per_member}
                    </p>
                  </div>

                  {/* Shop Info */}
                  {(discount.shop_hours || discount.contact_phone) && (
                    <div className="bg-slate-800/50 rounded-lg p-3 mb-3 space-y-2">
                      {discount.shop_hours && (
                        <div className="flex items-center gap-2 text-sm text-slate-300">
                          <Clock className="w-4 h-4 text-slate-400" />
                          <span>{discount.shop_hours}</span>
                        </div>
                      )}
                      {discount.contact_phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-slate-400" />
                          <a 
                            href={`tel:${discount.contact_phone}`}
                            className="text-yellow-400 hover:underline"
                          >
                            {discount.contact_phone}
                          </a>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Notes */}
                  {discount.notes && (
                    <p className="text-xs text-slate-400 italic mb-3 bg-slate-800/30 p-2 rounded">
                      {discount.notes}
                    </p>
                  )}

                  {/* Product Link */}
                  {discount.product_url && (
                    <a 
                      href={discount.product_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block mb-4 text-sm text-yellow-400 hover:text-yellow-300 hover:underline"
                      data-testid={`product-link-${discount.id}`}
                    >
                      View Product Details →
                    </a>
                  )}

                  <Button
                    onClick={() => generateQRCode(discount)}
                    className="w-full bg-yellow-500/100 hover:bg-yellow-400 text-black font-semibold"
                    data-testid={`generate-qr-button-${discount.id}`}
                  >
                    Generate QR Code
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* QR Code Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="max-w-md" data-testid="qr-code-dialog">
          <DialogHeader>
            <DialogTitle>Your Discount QR Code</DialogTitle>
          </DialogHeader>
          {selectedDiscount && qrCode && (
            <div className="text-center space-y-4">
              <div className="bg-[#0a0f1a] p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-2" data-testid="qr-dialog-title">{selectedDiscount.title}</h3>
                <p className="text-slate-400 mb-4" data-testid="qr-dialog-code">Code: {selectedDiscount.discount_code}</p>
              </div>
              
              <div className="flex justify-center">
                <div className="bg-slate-900/50 p-4 rounded-lg border-4 border-yellow-500/30">
                  <img
                    src={`data:image/png;base64,${qrCode}`}
                    alt="Discount QR Code"
                    className="w-64 h-64"
                    data-testid="qr-code-image"
                  />
                </div>
              </div>

              <div className="bg-yellow-500/10 p-4 rounded-lg">
                <p className="text-sm text-slate-300">
                  Show this QR code to the merchant to redeem your discount. This QR code is unique and can only be used once.
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
